/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  ForwardedRef,
  useRef,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DropdownSearch,
  DropdownSearchListItem,
  SavedLocations,
  LGBDatepicker,
  ValidationToast,
  Button,
  TableSkeletonsByAmount,
  LocationModal,
  HelpLabel,
  StoryblokHelpBlock,
} from "@components";
import { Badge, Label } from "flowbite-react";
import {
  HiOutlinePlus,
  HiOutlineLocationMarker,
  HiOutlinePencil,
  HiOutlineX,
  HiOutlineChevronUp,
  HiOutlineCalendar,
  HiOutlineTrash,
  HiOutlinePhone,
  HiOutlineUser,
  HiOutlineArrowUp,
  HiOutlineArrowDown,
} from "react-icons/hi";
import {
  ILocation,
  IRouteItem,
  IRouteTiming,
  Timestamp,
} from "../../../../types";
import { useWorkspaceData } from "@data";
import { useWorkspace } from "../../../../context/WorkspaceContext";
import { useCache } from "../../../../context/CacheContext";
import { Location } from "../../../../models";
import {
  ContactPersonDialog,
  AddEmptyStopDialog,
} from "./components/RouteStopDialogs";
import { formatStopTimingLabel } from "../../../../helpers/routeMappers";
import { StopTimeWindowsEditor } from "./components/StopTimeWindowsEditor";
import {
  cn,
  fieldFullWidth,
  surfaceDeliveryRoute,
  surfacePickupRoute,
  surfaceWarningRoute,
  wizardPanel,
  wizardPanelCentered,
  Stack,
} from "@design-system";

export interface RouteSelectionProps {
  route: IRouteItem[];
  validationFailed: boolean;
  hideSearch?: boolean;
  /** Template mode: hide per-stop dates (templates carry no static dates). */
  hideDates?: boolean;
  expandStopsByDefault?: boolean;
  /** When set, hide (but keep) stops that are the workspace default pickup at
   * this location id, so the fixed depot pickup isn't shown in the stop list. */
  hideDefaultPickupLocationId?: string;
  update: (routes: IRouteItem[]) => void;
}
export interface RouteSelectionRef {
  updateOrderRoute: () => void;
}

const RouteSelection = forwardRef(
  (props: RouteSelectionProps, ref: ForwardedRef<RouteSelectionRef>) => {
    const { t } = useTranslation("orders");
    const { activeWorkspace } = useWorkspace();
    const { updateCacheKey } = useCache();
    const [showAllLocations, setShowAllLocations] = useState(false);
    const [currentRoute, setCurrentRoute] = useState(props.route);
    const [searchText, setSearchText] = useState<string>("");
    const [searchResults, setSearchResults] = useState([] as ILocation[]);
    const [isInsertModalOpen, openInsertModal] = useState(false);
    const [locationData, setLocationData] = useState(
      Location.defaultLocation(),
    );
    const [inputFocused, setInputFocused] = useState(false);
    const [expandedRouteItems, setExpandedRouteItems] = useState<number[]>([]);
    const [showValidationToast, setShowValidationToast] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");

    // Bruk en ref i stedet for state for å holde toggle-tilstanden
    const shouldSaveLocationRef = useRef<boolean>(true);

    // Saved locations come from the centrally-loaded workspace data store.
    const { locations, loading } = useWorkspaceData();

    // Kontaktperson dialog
    const [showContactDialog, setShowContactDialog] = useState(false);
    const [contactDialogIndex, setContactDialogIndex] = useState<number | null>(
      null,
    );

    // Dialog for å legge til ny lokasjon
    const [showAddLocationDialog, setShowAddLocationDialog] = useState(false);

    // Sjekk om enheten er mobil ved å bruke en CSS-mediaquery variabel
    const [isMobile, setIsMobile] = useState(false);

    // Oppdater isMobile state når vindusstørrelsen endres
    useEffect(() => {
      const checkIfMobile = () => {
        setIsMobile(window.innerWidth < 768);
      };

      // Kjør sjekken på oppstart
      checkIfMobile();

      // Legg til lytter for vindusendringer
      window.addEventListener("resize", checkIfMobile);

      // Fjern lytteren når komponenten avmonteres
      return () => {
        window.removeEventListener("resize", checkIfMobile);
      };
    }, []);

    // Funksjon for å generere tidsalternativer for hver 15. minutt
    const generateTimeOptions = (): string[] => {
      const times: string[] = [];
      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          const formattedHour = hour.toString().padStart(2, "0");
          const formattedMinute = minute.toString().padStart(2, "0");
          times.push(`${formattedHour}:${formattedMinute}`);
        }
      }
      return times;
    };

    // When props.route changes, update the current route.
    useEffect(() => {
      setCurrentRoute(props.route);
    }, [props.route]);

    // Update search results based on searchText and input focus.
    useEffect(() => {
      if (searchText.length) {
        // When something is typed, filter the available locations.
        const res =
          locations?.filter((x) =>
            (x.addressLine + " " + x.displayName)
              .toLowerCase()
              .includes(searchText.toLowerCase()),
          ) || [];
        setSearchResults(res.slice(0, 50));
      } else if (inputFocused) {
        // When input is focused and empty, show all available locations.
        setSearchResults(locations?.slice(0, 50) || []);
      } else {
        setSearchResults([]);
      }
    }, [searchText, locations, inputFocused, props.hideSearch]);

    function toggleShowAllDialog() {
      setSearchText("");
      setShowAllLocations(!showAllLocations);
    }

    function handleSearch(val: string) {
      setSearchText(val);
    }

    // When the dropdown input is focused, set inputFocused to true.
    function handleFocus() {
      setInputFocused(true);
    }

    // Delay hiding the dropdown on blur to allow click events to process.
    function handleBlur() {
      setTimeout(() => {
        setInputFocused(false);
      }, 150); // Adjust the delay as needed.
    }

    /**
     * Exposed to the parent so it can be called through ref.
     */
    useImperativeHandle(ref, () => ({
      updateOrderRoute,
    }));

    const updateOrderRoute = () => {
      // Bruk currentRoute direkte i stedet for å gå via routeRefs
      // Dette sikrer at vi alltid har oppdaterte data, selv om kortene ikke er utvidet
      props.update([...currentRoute]);
    };

    const removeRouteItem = (routeIdx: number) => {
      const stopToRemove = currentRoute.at(routeIdx);
      const route = currentRoute.filter(
        (x) => x.stopNumber !== stopToRemove?.stopNumber,
      );
      // Re-number the stops.
      for (let i = 0; i < route.length; i++) {
        route[i].stopNumber = i;
      }
      setCurrentRoute(route);
      // Remove from expanded items if it was expanded
      setExpandedRouteItems(
        expandedRouteItems
          .filter((idx) => idx !== routeIdx)
          .map((idx) => (idx > routeIdx ? idx - 1 : idx)),
      );
      // Update parent component
      props.update(route);
    };

    // Validerer at ruten har riktig rekkefølge av henting og levering
    const validateRouteOrder = (route: IRouteItem[]): boolean => {
      if (route.length === 0) return true;

      // Første stopp må være en henting
      if (!route[0].isPickup) {
        setValidationMessage(
          t("create.route_selection.first_stop_must_be_pickup"),
        );
        setShowValidationToast(true);
        return false;
      }

      // Sjekk at vi har minst én henting før en levering
      let hasPickup = false;

      for (let i = 0; i < route.length; i++) {
        if (route[i].isPickup) {
          hasPickup = true;
        } else if (!hasPickup) {
          // Hvis vi har en levering uten en tidligere henting
          setValidationMessage(
            t("create.route_selection.delivery_before_pickup"),
          );
          setShowValidationToast(true);
          return false;
        }
      }

      return true;
    };

    // Oppdatert togglePickupDelivery funksjon med validering
    const togglePickupDelivery = (
      index: number,
      isPickup: boolean | undefined,
    ) => {
      const updatedRoute = [...currentRoute];
      // Sikre at isPickup alltid er en boolean
      const pickupValue = isPickup === undefined ? false : isPickup;
      const updatedRouteItem = {
        ...updatedRoute[index],
        isPickup: pickupValue,
      };

      // Lag en kopi av ruten med den oppdaterte verdien for validering
      const routeForValidation = [...updatedRoute];
      routeForValidation[index] = updatedRouteItem;

      // Valider den nye ruten
      if (!validateRouteOrder(routeForValidation)) {
        return; // Ikke oppdater hvis valideringen feiler
      }

      // Oppdater ruten hvis valideringen er OK
      updatedRoute[index] = updatedRouteItem;
      setCurrentRoute(updatedRoute);
      // Oppdater parent-komponenten
      props.update(updatedRoute);
    };

    // Oppdater updateRouteItem for å inkludere validering
    const updateRouteItem = (routeIdx: number, val: IRouteItem) => {
      // Hvis isPickup har endret seg, bruk togglePickupDelivery i stedet
      if (val.isPickup !== currentRoute[routeIdx].isPickup) {
        togglePickupDelivery(routeIdx, val.isPickup);
        return;
      }

      const route = [...currentRoute];
      route[routeIdx] = val;
      setCurrentRoute(route);
      // Oppdater parent-komponenten
      props.update(route);
    };

    // Funksjon for å flytte et stopp opp i rekkefølgen
    const moveStopUp = (index: number) => {
      if (index <= 0) return; // Kan ikke flytte første element opp

      const updatedRoute = [...currentRoute];
      // Bytt stopNumber mellom dette og forrige element
      const currentStopNumber = updatedRoute[index].stopNumber;
      updatedRoute[index].stopNumber = updatedRoute[index - 1].stopNumber;
      updatedRoute[index - 1].stopNumber = currentStopNumber;

      // Bytt posisjon i arrayet
      [updatedRoute[index], updatedRoute[index - 1]] = [
        updatedRoute[index - 1],
        updatedRoute[index],
      ];

      // Valider den nye ruten
      if (!validateRouteOrder(updatedRoute)) {
        return; // Ikke oppdater hvis valideringen feiler
      }

      setCurrentRoute(updatedRoute);
      // Oppdater parent-komponenten
      props.update(updatedRoute);
    };

    // Funksjon for å flytte et stopp ned i rekkefølgen
    const moveStopDown = (index: number) => {
      if (index >= currentRoute.length - 1) return; // Kan ikke flytte siste element ned

      const updatedRoute = [...currentRoute];
      // Bytt stopNumber mellom dette og neste element
      const currentStopNumber = updatedRoute[index].stopNumber;
      updatedRoute[index].stopNumber = updatedRoute[index + 1].stopNumber;
      updatedRoute[index + 1].stopNumber = currentStopNumber;

      // Bytt posisjon i arrayet
      [updatedRoute[index], updatedRoute[index + 1]] = [
        updatedRoute[index + 1],
        updatedRoute[index],
      ];

      // Valider den nye ruten
      if (!validateRouteOrder(updatedRoute)) {
        return; // Ikke oppdater hvis valideringen feiler
      }

      setCurrentRoute(updatedRoute);
      // Oppdater parent-komponenten
      props.update(updatedRoute);
    };

    // Handle creation/updating of a location.
    const handleCreateOrUpdate = (location: ILocation) => {
      addSavedLocation(location.id);
      openInsertModal(false);
    };

    // Finn CreateDialog-komponenten og oppdater den
    const CreateDialog = (
      <LocationModal
        data={locationData}
        isShowing={isInsertModalOpen}
        showSaveToggle={true}
        shouldSaveLocation={shouldSaveLocationRef.current}
        onSaveToggleChange={(shouldSave) => {
          shouldSaveLocationRef.current = shouldSave;
        }}
        onConfirm={(location: ILocation) => handleCreateOrUpdate(location)}
        onCancel={() => openInsertModal(false)}
      />
    );

    const showAllLocationsDialog = (
      <Dialog
        title={t("create.route_selection.add_saved_location_title")}
        show={showAllLocations}
        dismissible
        toggleModal={() => toggleShowAllDialog()}
        hideButtons
        content={
          <SavedLocations
            locations={locations || []}
            currentRoute={currentRoute}
            addLocation={addSavedLocation}
            close={toggleShowAllDialog}
          />
        }
      />
    );

    // Oppdater addSavedLocation for å inkludere validering
    function addSavedLocation(id: string) {
      const location = locations?.find((x) => x.id === id);
      let newRoute = [...currentRoute];

      if (!location) {
        console.error("Kunne ikke finne lokasjon med ID:", id);
        return;
      }

      const defaultContacts = (location.contacts || []).map((contact) => ({
        name: contact.name,
        phoneNumber: contact.phoneNumber,
        location: location.displayName,
      }));
      const locationWithoutContacts: Partial<ILocation> = { ...location };
      delete locationWithoutContacts.contacts;

      // Create a clone of the location with a unique ID for the route
      const uniqueId = `${location.id}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      const uniqueLocation = {
        ...locationWithoutContacts,
        id: uniqueId,
      };

      // Store the original ID in a custom property
      (uniqueLocation as any).originalLocationId = location.id;

      // Marker lokasjonen som lagret
      (uniqueLocation as any).isSaved = true;

      // Bestem om dette skal være en henting eller levering
      const shouldBePickup =
        currentRoute.length === 0 ||
        !currentRoute.some((item) => item.isPickup);

      if (currentRoute.length === 1 && !currentRoute[0].isPickup) {
        newRoute = [
          {
            location: uniqueLocation as ILocation,
            stopNumber: currentRoute.length,
            stopDate: Timestamp.now(),
            timing: { earliest: true } as IRouteTiming,
            isPickup: true, // Første stopp eller hvis ingen henting finnes ennå
            contact: defaultContacts.length ? defaultContacts : undefined,
          },
          ...currentRoute,
        ];
      } else {
        newRoute = newRoute.concat([
          {
            location: uniqueLocation as ILocation,
            stopNumber: currentRoute.length,
            stopDate: Timestamp.now(),
            timing: { earliest: true } as IRouteTiming,
            isPickup: shouldBePickup, // Første stopp eller hvis ingen henting finnes ennå
            contact: defaultContacts.length ? defaultContacts : undefined,
          },
        ]);
      }

      // Sikre at stopNumber alltid samsvarer med rekkefølgen i arrayet,
      // slik at oppsummering/persistering (som sorterer på stopNumber)
      // ikke havner ut av synk med listen (f.eks. levering før henting).
      newRoute = newRoute.map((item, index) => ({
        ...item,
        stopNumber: index,
      }));

      // Valider at ruten har riktig rekkefølge (henting før levering)
      if (!validateRouteOrder(newRoute)) {
        return; // Ikke oppdater hvis valideringen feiler
      }

      setSearchText("");
      if (showAllLocations) {
        toggleShowAllDialog();
      }
      setCurrentRoute(newRoute);
      // Oppdater parent-komponenten
      props.update(newRoute);
    }

    function addRow() {
      // Åpne dialogen med riktig defaultIsPickup verdi
      setShowAddLocationDialog(true);
    }

    // Oppdater addNewLocationFromDialog for å inkludere validering
    function addNewLocationFromDialog(
      name: string,
      address: string,
      postCode: string,
      city: string,
      country: string,
      description: string,
      isPickup: boolean,
      date: Date,
      timingOption: string,
      shouldSave: boolean,
    ) {
      // Tving første stopp til å være henting
      if (currentRoute.length === 0 && !isPickup) {
        isPickup = true; // Overstyr brukerens valg for første stopp
      }

      // Hvis dette ikke er en henting og vi ikke har noen henting ennå, vis feilmelding
      if (!isPickup && !currentRoute.some((item) => item.isPickup)) {
        setValidationMessage(t("create.route_selection.need_pickup_first"));
        setShowValidationToast(true);
        return;
      }

      let timing: IRouteTiming;

      switch (timingOption) {
        case "1":
          timing = { earliest: false, latest: false, before: "17:00" };
          break;
        case "2":
          timing = {
            earliest: false,
            latest: false,
            before: "17:00",
            after: "08:00",
          };
          break;
        case "3":
          timing = { earliest: false, latest: false, after: "08:00" };
          break;
        case "4":
          timing = { earliest: true, latest: false };
          break;
        case "5":
          timing = { earliest: false, latest: true };
          break;
        default:
          timing = { earliest: true, latest: false };
      }

      // Opprett et nytt lokasjonsobjekt med all informasjon
      const location: ILocation = {
        id: "", // Midlertidig ID
        displayName: name || t("create.route_selection.unnamed_location"),
        addressLine: address,
        postCode: postCode,
        city: city,
        countryCode: country,
        description: description,
        workspaceId: activeWorkspace?.workspaceId || "",
        customerId: "",
        createdAt: Timestamp.now(),
      };

      // Hvis lokasjonen skal lagres, gjør det først
      if (shouldSave) {
        // Opprett et Location-objekt for å lagre
        const locationToSave = new Location({ ...location });

        // Vis laster-indikator
        // TODO: Legg til en laster-indikator her

        // Lagre lokasjonen i databasen
        Location.create(locationToSave)
          .then((res: ILocation | null) => {
            if (res) {
              // Marker lokasjonen som lagret
              (res as any).isSaved = true;

              // Bust the cache so the centrally-loaded location list refreshes
              setTimeout(() => {
                updateCacheKey();
              }, 500);
              // Legg til stoppet i ruten
              addStopToRoute(res, isPickup, date, timing);
            } else {
              console.error("Kunne ikke lagre lokasjon i databasen");
              // Marker lokasjonen som ikke lagret
              (location as any).isSaved = false;
              addStopToRoute(location, isPickup, date, timing);
            }
          })
          .catch((error) => {
            console.error("Feil ved lagring av lokasjon:", error);
            // Marker lokasjonen som ikke lagret
            (location as any).isSaved = false;
            // Legg til stoppet i ruten selv om lagringen feilet
            addStopToRoute(location, isPickup, date, timing);
          });
      } else {
        // Marker lokasjonen som ikke lagret
        (location as any).isSaved = false;

        // Legg til stoppet i ruten uten å lagre lokasjonen
        addStopToRoute(location, isPickup, date, timing);
      }

      // Lukk dialogen
      setShowAddLocationDialog(false);
    }

    // Hjelpefunksjon for å legge til et stopp i ruten
    function addStopToRoute(
      location: ILocation,
      isPickup: boolean,
      date: Date,
      timing: IRouteTiming,
    ) {
      const defaultContacts = (location.contacts || []).map((contact) => ({
        name: contact.name,
        phoneNumber: contact.phoneNumber,
        location: location.displayName,
      }));
      const locationWithoutContacts: Partial<ILocation> = { ...location };
      delete locationWithoutContacts.contacts;
      location = locationWithoutContacts as ILocation;

      // Generate a unique ID for this location instance if it's being added to a route
      // This allows the same location to be used multiple times in the route
      if (!location.id.includes("_")) {
        const uniqueId = `${location.id || "temp"}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

        // Store the original ID in a way compatible with ILocation type
        const locationWithUniqueId = {
          ...location,
          id: uniqueId,
        };

        // Store original ID in a custom property that won't trigger TypeScript errors
        // We can still access it later if needed
        (locationWithUniqueId as any).originalLocationId = location.id;

        // Use the modified location
        location = locationWithUniqueId;
      }

      const newLocation: IRouteItem = {
        location: location,
        stopNumber: currentRoute.length,
        stopDate: Timestamp.fromDate(date),
        timing: timing,
        isPickup: isPickup,
        contact: defaultContacts.length ? defaultContacts : undefined,
      };

      const updatedRoute = [...currentRoute, newLocation];

      // Valider den nye ruten
      if (!validateRouteOrder(updatedRoute)) {
        return; // Ikke oppdater hvis valideringen feiler
      }
      setCurrentRoute(updatedRoute);
      // Oppdater parent-komponenten
      props.update(updatedRoute);
    }

    // Toggle expanded state for a route item
    const toggleExpandRouteItem = (index: number) => {
      if (expandedRouteItems.includes(index)) {
        setExpandedRouteItems(
          expandedRouteItems.filter((idx) => idx !== index),
        );
      } else {
        setExpandedRouteItems([...expandedRouteItems, index]);
      }
    };

    // Format date for display
    const formatDate = (timestamp: Timestamp) => {
      if (!timestamp) return "";
      const date = timestamp.toDate();
      return date.toLocaleDateString();
    };

    // Template mode: relative day offset of a stop vs the first stop (day 0).
    const getDayOffset = (index: number): number => {
      const base = currentRoute[0]?.stopDate;
      const stop = currentRoute[index]?.stopDate;
      if (!base || !stop) return 0;
      const diff = Timestamp.toMoment(stop)
        .startOf("day")
        .diff(Timestamp.toMoment(base).startOf("day"), "days");
      return Number.isFinite(diff) ? Math.max(0, diff) : 0;
    };

    // Template mode: set a stop's date to (first stop's date + offset days).
    const setDayOffset = (index: number, offset: number) => {
      const baseTs = currentRoute[0]?.stopDate ?? Timestamp.now();
      const newDate = Timestamp.toMoment(baseTs)
        .startOf("day")
        .add(offset, "days")
        .toDate();
      const updatedRoute = [...currentRoute];
      updatedRoute[index] = {
        ...updatedRoute[index],
        stopDate: Timestamp.fromDate(newDate),
      };
      setCurrentRoute(updatedRoute);
      props.update(updatedRoute);
    };

    // Template mode: day-offset selector shown in place of the date picker.
    // The first stop anchors the trip (day 0); later stops can be +N days.
    const renderDayOffset = (index: number) => {
      if (index === 0) {
        return (
          <div className="flex items-center text-sm text-secondary mb-1">
            <HiOutlineCalendar className="h-3.5 w-3.5 mr-1 flex-shrink-0 dark:text-white" />
            <span>{t("create.route_selection.same_day")}</span>
          </div>
        );
      }
      const offset = getDayOffset(index);
      return (
        <div className="mb-1">
          <Label
            className="dark:text-gray-300 mb-1 text-sm"
            htmlFor={`day-offset-${index}`}
            value={t("create.route_selection.day_offset")}
          />
          <select
            id={`day-offset-${index}`}
            className={fieldFullWidth}
            value={offset}
            onChange={(e) => setDayOffset(index, parseInt(e.target.value, 10))}
          >
            {Array.from({ length: 15 }, (_, d) => (
              <option key={d} value={d}>
                {d === 0
                  ? t("create.route_selection.same_day")
                  : t("create.route_selection.plus_days", { count: d })}
              </option>
            ))}
          </select>
        </div>
      );
    };

    // Get timing display text
    const getTimingText = (timing: IRouteTiming) =>
      formatStopTimingLabel(timing, t, { variant: "create-order" }) || "";

    // A stop is a hideable default pickup when it is a pickup at the workspace
    // default location. Internal orders clone locations with a `<id>_<ts>`
    // suffix, so match the exact id and that prefixed variant. The stop stays
    // in the route data; it is only hidden from the list, so an order made up
    // solely of the default pickup simply shows an empty selection until the
    // dispatcher adds a delivery.
    const hiddenPickupId = props.hideDefaultPickupLocationId;
    const isStopHidden = (item: IRouteItem) => {
      if (!hiddenPickupId || !item.isPickup) return false;
      const id = item.location?.id;
      if (!id) return false;
      return id === hiddenPickupId || id.startsWith(`${hiddenPickupId}_`);
    };
    const visibleStopCount = currentRoute.filter(
      (item) => !isStopHidden(item),
    ).length;

    // Render compact location card
    const renderCompactLocationCard = (
      routeItem: IRouteItem,
      index: number,
    ) => {
      // Hidden default pickup: keep it in the route data, just don't render it.
      if (isStopHidden(routeItem)) return null;

      // Number shown to the user counts only the visible stops.
      const displayNumber = currentRoute
        .slice(0, index + 1)
        .filter((item) => !isStopHidden(item)).length;
      // For manual creation, track explicitly collapsed cards so new stops open too.
      const isExpanded = props.expandStopsByDefault
        ? !expandedRouteItems.includes(index)
        : expandedRouteItems.includes(index);

      // Sjekk om lokasjonen er lagret i databasen
      // Sjekk først om isSaved-egenskapen er satt, deretter sjekk ID-formatet
      const isSavedLocation =
        (routeItem.location as any)?.isSaved === true ||
        (routeItem.location.id &&
          !routeItem.location.id.startsWith("temp-") &&
          routeItem.location.id.length > 10);

      // Sjekk om lokasjonen har gyldig adresse
      const hasValidAddress =
        routeItem.location.addressLine &&
        routeItem.location.postCode &&
        routeItem.location.city;

      // Funksjon for å endre om et stopp er henting eller levering
      const handleTogglePickupDelivery = (isPickup: boolean) => {
        togglePickupDelivery(index, isPickup);
      };

      // Bestem bakgrunnsfarge og border basert på om det er henting eller levering og om lokasjonen er lagret
      const getCardStyle = () => {
        if (isSavedLocation) {
          return routeItem.isPickup ? surfacePickupRoute : surfaceDeliveryRoute;
        }
        return surfaceWarningRoute;
      };

      const getNumberStyle = () => {
        return routeItem.isPickup
          ? "bg-brand text-white shadow-lg"
          : "bg-success text-white shadow-lg";
      };

      // Bestem tekst for stopp-nummer
      const getStopLabel = () => {
        return routeItem.isPickup ? t("pickup") : t("delivery");
      };

      return (
        <div
          key={`route-item-${index}`}
          className={`rounded-lg p-3 mb-3 transition-all duration-200 relative shadow-md hover:shadow-lg ${getCardStyle()} ${props.validationFailed && !hasValidAddress ? "border-red-500" : ""}`}
        >
          {/* Stopp-nummer indikator */}
          <div className="absolute -left-6 top-1/2 -translate-y-1/2 flex flex-col items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center font-bold shadow-md ${getNumberStyle()}`}
            >
              {displayNumber}
            </div>
          </div>

          {!isExpanded && (
            <div
              className={`${isMobile ? "grid grid-cols-1 gap-2" : "grid grid-cols-12 gap-2"} items-start`}
            >
              {/* Info-seksjon */}
              <div className={`${isMobile ? "mb-3" : "col-span-5"}`}>
                <div className="flex items-center mb-1">
                  <Badge
                    color={routeItem.isPickup ? "info" : "success"}
                    className="mr-2"
                  >
                    {getStopLabel()}
                  </Badge>
                  {isSavedLocation ? (
                    <span className="text-xs text-success">
                      ({t("create.route_selection.saved")})
                    </span>
                  ) : (
                    <span className="text-xs text-warning-contrast dark:text-lgb-yellow-400">
                      ({t("create.route_selection.not_saved")})
                    </span>
                  )}
                </div>
                <h3 className="text-md font-medium text-text truncate">
                  {routeItem.location.displayName ||
                    t("create.route_selection.unnamed_location")}
                </h3>
                <div className="flex items-center text-sm text-secondary text-secondary">
                  <HiOutlineLocationMarker className="h-3.5 w-3.5 mr-1 flex-shrink-0 dark:text-white" />
                  <span className="truncate">
                    {routeItem.location.addressLine ||
                      t("create.route_selection.no_address")}
                  </span>
                </div>
              </div>

              {/* Dato og timing */}
              <div className={`${isMobile ? "mb-3" : "col-span-4"}`}>
                {!props.hideDates ? (
                  <div className="flex items-center text-sm text-secondary text-secondary mb-1">
                    <HiOutlineCalendar className="h-3.5 w-3.5 mr-1 flex-shrink-0 dark:text-white" />
                    <span>{formatDate(routeItem.stopDate)}</span>
                  </div>
                ) : (
                  renderDayOffset(index)
                )}
                <div className="text-sm text-secondary text-secondary">
                  {getTimingText(routeItem.timing)}
                </div>

                {/* Pickup/Delivery toggler */}
                <div className={`flex ${isMobile ? "mt-3" : "mt-2"} space-x-4`}>
                  <div className="flex items-center">
                    <input
                      id={`pickup-${index}`}
                      type="radio"
                      name={`stopType-${index}`}
                      className="w-4 h-4 text-brand bg-gray-100 border-gray-300 focus:ring-brand dark:focus:ring-brand dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-[#2c3353]"
                      checked={routeItem.isPickup === true}
                      onChange={() => handleTogglePickupDelivery(true)}
                    />
                    <label
                      htmlFor={`pickup-${index}`}
                      className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      {t("pickup")}
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id={`delivery-${index}`}
                      type="radio"
                      name={`stopType-${index}`}
                      className="w-4 h-4 text-brand bg-gray-100 border-gray-300 focus:ring-brand dark:focus:ring-brand dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-[#2c3353]"
                      checked={routeItem.isPickup === false}
                      onChange={() => handleTogglePickupDelivery(false)}
                    />
                    <label
                      htmlFor={`delivery-${index}`}
                      className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      {t("delivery")}
                    </label>
                  </div>
                </div>
              </div>

              {/* Handlingsknapper */}
              <div
                className={`${isMobile ? "flex items-center justify-between" : "col-span-3 flex justify-end items-center"} space-x-1`}
              >
                {/* Rekkefølge-knapper - bare på desktop */}
                {!isMobile && (
                  <div className="flex flex-col mr-2">
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => moveStopUp(index)}
                      disabled={index === 0}
                      className="p-1 mb-1 hover:bg-surface-muted dark:hover:bg-surface-hover disabled:opacity-50"
                    >
                      <HiOutlineArrowUp className="h-3.5 w-3.5 dark:text-white" />
                    </Button>
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => moveStopDown(index)}
                      disabled={index === currentRoute.length - 1}
                      className="p-1 hover:bg-surface-muted dark:hover:bg-surface-hover disabled:opacity-50"
                    >
                      <HiOutlineArrowDown className="h-3.5 w-3.5 dark:text-white" />
                    </Button>
                  </div>
                )}

                {/* På mobil, bruk ikonknapper i stedet for tekst for å spare plass */}
                <Button
                  size="xs"
                  color="light"
                  onClick={() => toggleExpandRouteItem(index)}
                  className={`p-1 hover:bg-surface-muted dark:hover:bg-surface-hover ${isMobile ? "p-2 w-auto h-auto flex items-center" : ""}`}
                >
                  {isMobile ? (
                    <>
                      <HiOutlinePencil className="h-4 w-4 dark:text-white mr-1" />
                      <span className="text-xs">Rediger</span>
                    </>
                  ) : (
                    <>
                      <HiOutlinePencil className="mr-1 h-3.5 w-3.5 dark:text-white" />
                      {t("common:edit")}
                    </>
                  )}
                </Button>

                {/* Opp/ned knapper på mobil */}
                {isMobile && (
                  <>
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => moveStopUp(index)}
                      disabled={index === 0}
                      className="p-2 hover:bg-surface-muted dark:hover:bg-surface-hover disabled:opacity-50 w-10 h-10"
                    >
                      <HiOutlineArrowUp className="h-4 w-4 dark:text-white" />
                    </Button>
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => moveStopDown(index)}
                      disabled={index === currentRoute.length - 1}
                      className="p-2 hover:bg-surface-muted dark:hover:bg-surface-hover disabled:opacity-50 w-10 h-10"
                    >
                      <HiOutlineArrowDown className="h-4 w-4 dark:text-white" />
                    </Button>
                  </>
                )}

                <Button
                  size="xs"
                  color="light"
                  onClick={() => removeRouteItem(index)}
                  className={`p-1 hover:bg-surface-muted dark:hover:bg-surface-hover ${isMobile ? "p-2 w-10 h-10" : ""}`}
                >
                  {isMobile ? (
                    <HiOutlineTrash className="h-4 w-4 dark:text-white" />
                  ) : (
                    <HiOutlineTrash className="h-3.5 w-3.5 dark:text-white" />
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Expanded view with full RouteCard */}
          {isExpanded && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center">
                  <Badge
                    color={routeItem.isPickup ? "info" : "success"}
                    className="mr-2"
                  >
                    {getStopLabel()}
                  </Badge>
                  {isSavedLocation ? (
                    <span className="text-xs text-success">
                      ({t("create.route_selection.saved")})
                    </span>
                  ) : (
                    <span className="text-xs text-warning-contrast dark:text-lgb-yellow-400">
                      ({t("create.route_selection.not_saved")})
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {/* Rekkefølge-knapper */}
                  <div className="flex space-x-1 mr-2">
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => moveStopUp(index)}
                      disabled={index === 0}
                      className="p-1 hover:bg-surface-muted dark:hover:bg-surface-hover disabled:opacity-50"
                    >
                      <HiOutlineArrowUp className="h-3.5 w-3.5 dark:text-white" />
                    </Button>
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => moveStopDown(index)}
                      disabled={index === currentRoute.length - 1}
                      className="p-1 hover:bg-surface-muted dark:hover:bg-surface-hover disabled:opacity-50"
                    >
                      <HiOutlineArrowDown className="h-3.5 w-3.5 dark:text-white" />
                    </Button>
                  </div>
                  <button
                    onClick={() => toggleExpandRouteItem(index)}
                    className="p-1 text-muted hover:text-brand dark:text-muted dark:hover:text-white"
                    aria-label="Collapse"
                  >
                    <HiOutlineChevronUp className="h-5 w-5 dark:text-white" />
                  </button>
                </div>
              </div>

              <div className="mt-2 pt-2 border-t border-border-subtle dark:border-[#2c3353]">
                {routeItem.requiredTimeWindows != null || Boolean(routeItem.preferredTimeWindows?.length) ?
                  <StopTimeWindowsEditor stop={routeItem} onChange={(stop) => updateRouteItem(index, stop)} /> : null}
                <div className="bg-transparent dark:bg-transparent rounded-lg p-4 shadow-sm">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {!props.hideDates ? (
                      <div>
                        <Label
                          className="dark:text-gray-300 mb-2"
                          htmlFor={`date-${index}`}
                          value={t("create.route_selection.date")}
                        />
                        <LGBDatepicker
                          inputId={`date-${index}`}
                          selectedDate={
                            routeItem.stopDate
                              ? routeItem.stopDate.toDate()
                              : undefined
                          }
                          select={(newDate) => {
                            const updatedRouteItem = {
                              ...routeItem,
                              stopDate: Timestamp.fromDate(newDate),
                            };
                            updateRouteItem(index, updatedRouteItem);
                          }}
                        />
                      </div>
                    ) : (
                      <div>{renderDayOffset(index)}</div>
                    )}

                    <div>
                      <Label
                        className="dark:text-gray-300 mb-2"
                        htmlFor={`timing-select-${index}`}
                        value={t("create.route_selection.timing")}
                      />
                      <select
                        id={`timing-select-${index}`}
                        disabled={routeItem.requiredTimeWindows != null}
                        className={fieldFullWidth}
                        value={
                          routeItem.timing.earliest
                            ? "4"
                            : routeItem.timing.latest
                              ? "5"
                              : routeItem.timing.before &&
                                  routeItem.timing.after
                                ? "2"
                                : routeItem.timing.before
                                  ? "1"
                                  : routeItem.timing.after
                                    ? "3"
                                    : "4"
                        }
                        onChange={(e) => {
                          let newTiming: IRouteTiming = {
                            earliest: false,
                            latest: false,
                          };
                          switch (e.target.value) {
                            case "1":
                              newTiming = {
                                earliest: false,
                                latest: false,
                                before: routeItem.timing.before || "17:00",
                              };
                              break;
                            case "2":
                              newTiming = {
                                earliest: false,
                                latest: false,
                                before: routeItem.timing.before || "17:00",
                                after: routeItem.timing.after || "08:00",
                              };
                              break;
                            case "3":
                              newTiming = {
                                earliest: false,
                                latest: false,
                                after: routeItem.timing.after || "08:00",
                              };
                              break;
                            case "4":
                              newTiming = { earliest: true, latest: false };
                              break;
                            case "5":
                              newTiming = { earliest: false, latest: true };
                              break;
                          }
                          const updatedRouteItem = {
                            ...routeItem,
                            timing: newTiming,
                          };
                          updateRouteItem(index, updatedRouteItem);
                        }}
                      >
                        <option value="1">
                          {t("create.route_selection.before")}
                        </option>
                        <option value="2">
                          {t("create.route_selection.between")}
                        </option>
                        <option value="3">
                          {t("create.route_selection.after")}
                        </option>
                        <option value="4">
                          {t("create.route_selection.earliest_possible")}
                        </option>
                        <option value="5">
                          {t("create.route_selection.latest_possible")}
                        </option>
                      </select>

                      {/* Tidsvelgere for før/etter/før og etter */}
                      {routeItem.requiredTimeWindows == null && (routeItem.timing.before || routeItem.timing.after) && (
                        <div className="mt-3 grid grid-cols-1 gap-3">
                          {routeItem.timing.after && (
                            <div>
                              <Label
                                className="dark:text-gray-300 mb-2 text-sm"
                                htmlFor={`after-time-${index}`}
                                value={t("create.route_selection.after")}
                              />
                              <select
                                id={`after-time-${index}`}
                                className={fieldFullWidth}
                                value={routeItem.timing.after}
                                onChange={(e) => {
                                  const newTiming = {
                                    ...routeItem.timing,
                                    after: e.target.value,
                                  };
                                  const updatedRouteItem = {
                                    ...routeItem,
                                    timing: newTiming,
                                  };
                                  updateRouteItem(index, updatedRouteItem);
                                }}
                              >
                                {generateTimeOptions().map((time) => (
                                  <option key={`after-${time}`} value={time}>
                                    {time}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          {routeItem.timing.before && (
                            <div>
                              <Label
                                className="dark:text-gray-300 mb-2 text-sm"
                                htmlFor={`before-time-${index}`}
                                value={t("create.route_selection.before")}
                              />
                              <select
                                id={`before-time-${index}`}
                                className={fieldFullWidth}
                                value={routeItem.timing.before}
                                onChange={(e) => {
                                  const newTiming = {
                                    ...routeItem.timing,
                                    before: e.target.value,
                                  };
                                  const updatedRouteItem = {
                                    ...routeItem,
                                    timing: newTiming,
                                  };
                                  updateRouteItem(index, updatedRouteItem);
                                }}
                              >
                                {generateTimeOptions().map((time) => (
                                  <option key={`before-${time}`} value={time}>
                                    {time}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Lokasjonsinformasjon - kun redigerbar for ikke-lagrede lokasjoner */}
                  {!hasValidAddress && (
                    <div className="mb-4 border-t border-border-subtle dark:border-[#2c3353] pt-4">
                      <h4 className="text-md font-medium mb-3 dark:text-white">
                        {t("create.route_selection.location")}
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label
                            className="dark:text-gray-300 mb-2"
                            htmlFor={`location-name-${index}`}
                            value={t("create.route_selection.display_name")}
                          />
                          <input
                            type="text"
                            id={`location-name-${index}`}
                            className={fieldFullWidth}
                            placeholder={t(
                              "create.route_selection.display_name_ph",
                            )}
                            value={routeItem.location.displayName || ""}
                            onChange={(e) => {
                              const updatedLocation = {
                                ...routeItem.location,
                                displayName: e.target.value,
                              };
                              const updatedRouteItem = {
                                ...routeItem,
                                location: updatedLocation,
                              };
                              updateRouteItem(index, updatedRouteItem);
                            }}
                          />
                        </div>

                        <div>
                          <Label
                            className="dark:text-gray-300 mb-2"
                            htmlFor={`location-address-${index}`}
                            value={t("create.route_selection.address")}
                          />
                          <input
                            type="text"
                            id={`location-address-${index}`}
                            className={fieldFullWidth}
                            placeholder={t("create.route_selection.address_ph")}
                            value={routeItem.location.addressLine || ""}
                            onChange={(e) => {
                              const updatedLocation = {
                                ...routeItem.location,
                                addressLine: e.target.value,
                              };
                              const updatedRouteItem = {
                                ...routeItem,
                                location: updatedLocation,
                              };
                              updateRouteItem(index, updatedRouteItem);
                            }}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <Label
                            className="dark:text-gray-300 mb-2"
                            htmlFor={`location-postcode-${index}`}
                            value={t("create.route_selection.postal_code")}
                          />
                          <input
                            type="text"
                            id={`location-postcode-${index}`}
                            className={fieldFullWidth}
                            placeholder={t(
                              "create.route_selection.postal_code_ph",
                            )}
                            value={routeItem.location.postCode || ""}
                            onChange={(e) => {
                              const updatedLocation = {
                                ...routeItem.location,
                                postCode: e.target.value,
                              };
                              const updatedRouteItem = {
                                ...routeItem,
                                location: updatedLocation,
                              };
                              updateRouteItem(index, updatedRouteItem);
                            }}
                          />
                        </div>

                        <div>
                          <Label
                            className="dark:text-gray-300 mb-2"
                            htmlFor={`location-city-${index}`}
                            value={t("create.route_selection.city")}
                          />
                          <input
                            type="text"
                            id={`location-city-${index}`}
                            className={fieldFullWidth}
                            placeholder={t("create.route_selection.city_ph")}
                            value={routeItem.location.city || ""}
                            onChange={(e) => {
                              const updatedLocation = {
                                ...routeItem.location,
                                city: e.target.value,
                              };
                              const updatedRouteItem = {
                                ...routeItem,
                                location: updatedLocation,
                              };
                              updateRouteItem(index, updatedRouteItem);
                            }}
                          />
                        </div>

                        <div>
                          <Label
                            className="dark:text-gray-300 mb-2"
                            htmlFor={`location-country-${index}`}
                            value={t("create.route_selection.country")}
                          />
                          <select
                            id={`location-country-${index}`}
                            className={cn(fieldFullWidth, "mt-2")}
                            value={routeItem.location.countryCode || "NO"}
                            onChange={(e) => {
                              const updatedLocation = {
                                ...routeItem.location,
                                countryCode: e.target.value,
                              };
                              const updatedRouteItem = {
                                ...routeItem,
                                location: updatedLocation,
                              };
                              updateRouteItem(index, updatedRouteItem);
                            }}
                          >
                            <option value="NO">Norge</option>
                            <option value="SE">Sverige</option>
                            <option value="DK">Danmark</option>
                            <option value="FI">Finland</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mb-4">
                    <Label
                      className="dark:text-gray-300 mb-2"
                      htmlFor={`notes-${index}`}
                      value={t("common:comment")}
                    />
                    <textarea
                      id={`notes-${index}`}
                      rows={3}
                      className={fieldFullWidth}
                      placeholder={t("common:placeholder.description")}
                      value={routeItem.location.description || ""}
                      onChange={(e) => {
                        const updatedLocation = {
                          ...routeItem.location,
                          description: e.target.value,
                        };
                        const updatedRouteItem = {
                          ...routeItem,
                          location: updatedLocation,
                        };
                        updateRouteItem(index, updatedRouteItem);
                      }}
                    />
                  </div>

                  {/* Kontaktpersoner */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <Label
                        className="dark:text-gray-300"
                        htmlFor={`contacts-${index}`}
                        value={t("create.contact_info.title")}
                      />
                      <Button
                        size="xs"
                        color="light"
                        onClick={() => openContactDialog(index)}
                        className="p-1 hover:bg-surface-muted dark:hover:bg-surface-hover flex items-center"
                      >
                        <HiOutlineUser className="mr-1 h-3.5 w-3.5 dark:text-white" />
                        {isMobile ? "Legg til" : t("create.contact_info.title")}
                      </Button>
                    </div>

                    {/* Liste over kontaktpersoner */}
                    {routeItem.contact && routeItem.contact.length > 0 ? (
                      <div className="space-y-2 mt-2">
                        {routeItem.contact.map((person, contactIdx) => (
                          <div
                            key={contactIdx}
                            className="flex justify-between items-center p-3 bg-surface-muted/80 rounded-lg border border-transparent dark:border-[#2c3353]"
                          >
                            <div className="flex items-center">
                              <HiOutlineUser className="h-4 w-4 mr-2 text-secondary dark:text-white" />
                              <span className="text-sm font-medium text-text">
                                {person.name}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <HiOutlinePhone className="h-4 w-4 mr-1 text-secondary dark:text-white" />
                              <span className="text-sm mr-3 text-text">
                                {person.phoneNumber}
                              </span>
                              <button
                                onClick={() => {
                                  // Fjern kontaktperson
                                  const updatedContacts =
                                    routeItem.contact?.filter(
                                      (_, idx) => idx !== contactIdx,
                                    ) || [];
                                  const updatedRouteItem = {
                                    ...routeItem,
                                    contact: updatedContacts,
                                  };
                                  updateRouteItem(index, updatedRouteItem);
                                }}
                                className="text-secondary dark:text-muted hover:text-danger dark:hover:text-danger"
                              >
                                <HiOutlineX className="h-4 w-4 dark:text-white" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-secondary text-secondary italic mt-2">
                        {t("create.contact_info.no_contacts")}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end mt-4">
                    <button
                      onClick={() => toggleExpandRouteItem(index)}
                      className="text-sm px-4 py-2 text-secondary dark:text-white hover:text-brand dark:hover:text-brand border border-border-strong dark:border-[#2c3353] rounded-lg flex items-center transition-colors duration-200 bg-transparent dark:bg-brand-muted/20 hover:bg-surface-muted dark:hover:bg-brand-muted/40"
                    >
                      {t("common:close")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    };

    // Check if we have at least one pickup and one delivery location
    const hasRequiredLocations = currentRoute.length >= 2;

    // Funksjon for å åpne kontaktperson-dialogen
    const openContactDialog = useCallback((index: number) => {
      setContactDialogIndex(index);
      setShowContactDialog(true);
    }, []);

    // Funksjon for å legge til kontaktperson
    const handleAddContact = useCallback(
      (name: string, phone: string) => {
        if (contactDialogIndex !== null) {
          const routeItem = currentRoute[contactDialogIndex];
          const newContact = {
            name: name,
            phoneNumber: phone,
            location: routeItem.location.displayName || "",
          };

          const updatedContacts = routeItem.contact
            ? [...routeItem.contact, newContact]
            : [newContact];

          const updatedRouteItem = { ...routeItem, contact: updatedContacts };
          updateRouteItem(contactDialogIndex, updatedRouteItem);

          // Lukk dialogen
          setShowContactDialog(false);
        }
      },
      [contactDialogIndex, currentRoute, updateRouteItem],
    );

    return (
      <div>
        {/* Toast notification for validation errors */}
        <ValidationToast
          show={showValidationToast}
          title={t("create.route_selection.validation_error")}
          message={validationMessage}
          onClose={() => setShowValidationToast(false)}
          position="top-right"
        />

        {/* Initial view with search and add buttons */}
        <div className={`mb-6 ${wizardPanel}`}>
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-xl font-medium text-text">
              {t("create.route_selection.title")}
            </h2>
            <HelpLabel
              content={
                <StoryblokHelpBlock
                  storySlug="logibud-tms-web/help-popups/use-location"
                  accessToken="Jf9NMAu4DXeo3fwdjd1w7wtt"
                />
              }
              className="mt-1"
            />
          </div>

          <p className="text-secondary text-secondary mb-4">
            {t("create.route_selection.route_description_detail")}
          </p>

          {!props.hideSearch && (
            <div className="mb-4">
              <DropdownSearch
                value={searchText}
                placeholder={t("create.route_selection.search_ph")}
                inputChanged={handleSearch}
                showResults={searchResults.length > 0}
                onFocus={handleFocus}
                onBlur={handleBlur}
                childrenAbsolute
              >
                {loading.locations && <TableSkeletonsByAmount quantity={3} />}
                {!loading.locations && searchResults.length > 0 && (
                  <div className="max-h-80 overflow-y-auto overscroll-contain">
                    {searchResults.map((location: ILocation) => (
                      <DropdownSearchListItem
                        key={location.id}
                        id={location.id}
                        title={location.displayName}
                        subtitle={location.addressLine || ""}
                        buttonClickEvent={addSavedLocation}
                        inlineSubtitle
                        iconType="location"
                      />
                    ))}
                    {searchResults.length === 50 && (
                      <p
                        className="pt-4 text-md underline font-normal cursor-pointer hover:text-brand"
                        onClick={() => toggleShowAllDialog()}
                      >
                        {t("create.route_selection.see_all")}
                      </p>
                    )}
                  </div>
                )}
              </DropdownSearch>
            </div>
          )}

          {/* Add location buttons */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() => toggleShowAllDialog()}
              className="flex items-center border-border-subtle dark:border dark:border-gray-500"
              icon={<HiOutlineLocationMarker className="dark:text-white" />}
            >
              {isMobile
                ? "Lagrede lokasjoner"
                : t("create.route_selection.add_saved_location_button")}
            </Button>

            <Button
              variant="secondary"
              onClick={() => addRow()}
              className="flex items-center border-border-subtle dark:border dark:border-gray-500"
              icon={<HiOutlinePlus className="dark:text-white" />}
            >
              {isMobile ? "Ny lokasjon" : t("create.route_selection.add_stop")}
            </Button>
          </div>
        </div>

        {/* Selected locations section */}
        {visibleStopCount > 0 && (
          <div className="mb-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium dark:text-white">
                {t("create.route_selection.selected_locations")} (
                {visibleStopCount})
              </h3>

              {!hasRequiredLocations && (
                <Badge color="warning">
                  {t("create.route_selection.need_pickup_delivery")}
                </Badge>
              )}
            </div>

            <Stack gap={3} className="md:gap-6">
              {currentRoute.map((routeItem, index) =>
                renderCompactLocationCard(routeItem, index),
              )}
            </Stack>
          </div>
        )}

        {/* Empty state */}
        {visibleStopCount === 0 && (
          <div className={wizardPanelCentered}>
            <HiOutlineLocationMarker className="mx-auto h-12 w-12 text-muted dark:text-white" />
            <h3 className="mt-2 text-lg font-medium text-text">
              {t("create.route_selection.no_locations_yet")}
            </h3>
            <p className="mt-1 text-secondary dark:text-white">
              {t("create.route_selection.start_by_adding_location")}
            </p>
          </div>
        )}

        {CreateDialog}
        {showAllLocationsDialog}

        {/* Dialog for å legge til ny lokasjon */}
        <AddEmptyStopDialog
          isOpen={showAddLocationDialog}
          onClose={() => setShowAddLocationDialog(false)}
          onAdd={addNewLocationFromDialog}
          t={t}
          generateTimeOptions={generateTimeOptions}
          defaultIsPickup={currentRoute.length === 0}
          shouldSaveLocation={shouldSaveLocationRef.current}
          onSaveToggleChange={(shouldSave) => {
            shouldSaveLocationRef.current = shouldSave;
          }}
        />

        {/* Erstatt den gamle kontaktperson-dialogen med den nye komponenten */}
        <ContactPersonDialog
          isOpen={showContactDialog}
          onClose={() => setShowContactDialog(false)}
          onAdd={handleAddContact}
          t={t}
        />
      </div>
    );
  },
);

export default RouteSelection;
